import { useId, useState } from 'react';
import styled from 'styled-components';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import type { APIScopeData, ArdoqId } from '@ardoq/api-types';
import {
  BUTTON_CONTENT_SELECTOR,
  GhostButton,
  SmallGhostButton,
} from '@ardoq/button';
import { ComponentSelectorPane } from '@ardoq/component-hierarchies';
import { Box, FlexBox } from '@ardoq/layout';
import { ControlledPopover, POPOVER_WRAPPER_PADDING } from '@ardoq/popovers';
import { Text } from '@ardoq/typography';
import { t } from '@ardoq/i18n';
import { space } from '@ardoq/design-tokens';
import { changePresentationContext } from 'presentation/actions';
import { PRESENTATION_LIGHT_CHROME_CLASS } from 'presentation/chrome';
import { contextPicker$, type ContextPickerProps } from './contextPicker$';

const NO_IDS: string[] = [];
const TRIGGER_MAX_WIDTH = 240;

/**
 * `ComponentSelectorPane` lays itself out at `height: 100%`, which resolves to
 * zero inside the auto-height popover card.
 *
 * The horizontal margin cancels the popover card's padding
 * (`POPOVER_WRAPPER_PADDING`) so the search field and tree line up with the
 * heading and footer.
 */
const PickerSurface = styled(Box)`
  width: 360px;
  height: 400px;
  margin: calc(-1 * ${space.small}) -${POPOVER_WRAPPER_PADDING}px;
`;

const TriggerButton = styled(GhostButton)`
  max-width: ${TRIGGER_MAX_WIDTH}px;

  ${BUTTON_CONTENT_SELECTOR} > * {
    min-width: 0;
  }
`;

const ContextPickerBase = ({
  picker,
  locale,
  i18nLocale,
}: ContextPickerProps) => {
  const popoverId = useId();
  const [isOpen, setIsOpen] = useState(false);

  if (!picker) return null;
  const { search, contextComponentId, contextName, appliedSummary } = picker;

  const triggerLabel =
    contextName ??
    t(
      i18nLocale,
      'presentationAdmin:viewer.topBar.contextPicker.trigger',
      'Change context'
    );

  const triggerAriaLabel = contextName
    ? t(
        i18nLocale,
        'presentationAdmin:viewer.topBar.contextPicker.triggerWithSelection',
        { name: contextName },
        'Change context. Selected: {{name}}'
      )
    : undefined;

  const closePicker = () => setIsOpen(false);

  const selectContext = (nextContextComponentId: ArdoqId | null) => {
    dispatchAction(changePresentationContext(nextContextComponentId));
    closePicker();
  };

  const onComponentsSelected = ({ scopeComponents: [id] }: APIScopeData) => {
    if (id && id !== contextComponentId) selectContext(id);
  };

  return (
    <ControlledPopover
      id={popoverId}
      isOpen={isOpen}
      onClose={closePicker}
      onClickOutside={closePicker}
      placement={{ side: 'bottom', overflow: 'left' }}
      // The component tree only has light-mode styling, so a card inheriting
      // the dark chrome frames it in near-black. Portalling the card out of
      // the dark-mode scope keeps it on the light palette, matching the tree,
      // while the chrome scope class keeps slide shortcuts out of its way.
      forcePortal
      className={PRESENTATION_LIGHT_CHROME_CLASS}
      content={() => (
        <FlexBox flexDirection="column" gap="small">
          {search && (
            <>
              <Text variant="text2Bold" color="textDefault">
                {t(
                  i18nLocale,
                  'presentationAdmin:viewer.topBar.contextPicker.heading',
                  { typeName: search.typeName },
                  'Select {{typeName}}'
                )}
              </Text>
              <PickerSurface>
                <ComponentSelectorPane
                  componentQuery={search.query}
                  onComponentsSelected={onComponentsSelected}
                  selectedIds={
                    contextComponentId ? [contextComponentId] : NO_IDS
                  }
                  disabledOptions={NO_IDS}
                  placeholderText={t(
                    i18nLocale,
                    'presentationAdmin:viewer.topBar.contextPicker.searchPlaceholder',
                    { typeName: search.typeName },
                    'Search {{typeName}}...'
                  )}
                  locale={locale}
                  i18nLocale={i18nLocale}
                  autoFocus
                  isMultiselectable={false}
                />
              </PickerSurface>
            </>
          )}
          {contextComponentId && (
            <FlexBox align="center" justify="space-between" gap="small">
              {appliedSummary && (
                <Text variant="caption" color="textSubtle">
                  {t(
                    i18nLocale,
                    'presentationAdmin:viewer.topBar.contextPicker.appliedSummary',
                    { count: appliedSummary.total, ...appliedSummary },
                    appliedSummary.total === 1
                      ? 'Applied to {{applied}} of {{total}} slide'
                      : 'Applied to {{applied}} of {{total}} slides'
                  )}
                </Text>
              )}
              <SmallGhostButton onClick={() => selectContext(null)}>
                {t(
                  i18nLocale,
                  'presentationAdmin:viewer.topBar.contextPicker.reset',
                  'Reset context'
                )}
              </SmallGhostButton>
            </FlexBox>
          )}
        </FlexBox>
      )}
    >
      <TriggerButton
        isActive={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={triggerAriaLabel}
      >
        {triggerLabel}
      </TriggerButton>
    </ControlledPopover>
  );
};

export const ContextPicker = connect(ContextPickerBase, contextPicker$);
